/* Eloence Cart — pub-sub wrapper over Shopify /cart.js
   Listeners are notified on any state change. Shared across pages via
   localStorage cross-tab sync. Mutations (add/updateQty/remove) are
   applied optimistically: the predicted state is broadcast immediately,
   the network round-trip reconciles on resolve, and failures revert via
   `/cart.js` refresh. */
(function () {
  var STORAGE_KEY = 'eloence:cart:v1';
  var EMPTY_STATE = { items: [], item_count: 0, total_price: 0 };
  var listeners = [];
  var state = { items: [], item_count: 0, total_price: 0, currency: 'EUR' };
  var ready = false;
  var inflight = null;

  function debug(e) {
    if (window.console && console.debug) console.debug('[eloence]', e);
  }

  function notify() {
    for (var i = 0; i < listeners.length; i++) {
      try { listeners[i](state); } catch (e) { debug(e); }
    }
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ts: Date.now(), state: state }));
    } catch (e) { debug(e); }
  }

  function announce(message) {
    if (!message) return;
    try {
      window.dispatchEvent(new CustomEvent('eloence:cart:announce', { detail: { message: message } }));
    } catch (e) { debug(e); }
  }

  function isValidCart(data) {
    return !!data
      && typeof data.items === 'object'
      && data.items !== null
      && data.items.length !== undefined
      && typeof data.item_count === 'number';
  }

  function setState(data) {
    if (!isValidCart(data)) {
      /* Invalid / malformed response — refresh from source of truth */
      refresh();
      return state;
    }
    state = {
      items: data.items || [],
      item_count: data.item_count || 0,
      total_price: data.total_price || 0,
      currency: data.currency || state.currency,
      cart: data,
    };
    ready = true;
    notify();
    return state;
  }

  /* Optimistic state helpers — mutate a cloned snapshot, push it through
     the pub-sub pipeline, then rely on the network call to reconcile. */
  function cloneItem(it) {
    var c = {};
    for (var k in it) if (Object.prototype.hasOwnProperty.call(it, k)) c[k] = it[k];
    return c;
  }

  function recomputeTotals(items) {
    var count = 0;
    var total = 0;
    for (var i = 0; i < items.length; i++) {
      count += items[i].quantity || 0;
      /* line_price is in cents on Shopify cart JSON. Prefer final_line_price,
         fall back to original_line_price, then to price*qty. */
      var linePrice = items[i].final_line_price;
      if (linePrice == null) linePrice = items[i].original_line_price;
      if (linePrice == null) linePrice = (items[i].price || 0) * (items[i].quantity || 0);
      total += linePrice;
    }
    return { item_count: count, total_price: total };
  }

  function applyOptimistic(nextItems) {
    var totals = recomputeTotals(nextItems);
    state = {
      items: nextItems,
      item_count: totals.item_count,
      total_price: totals.total_price,
      currency: state.currency,
      cart: state.cart,
    };
    notify();
  }

  function fetchJSON(url, opts) {
    return fetch(url, Object.assign({
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
    }, opts || {})).then(function (r) {
      return r.json().then(function (body) {
        if (!r.ok) {
          var err = new Error((body && (body.description || body.message)) || ('Cart request failed: ' + r.status));
          err.status = r.status;
          err.body = body;
          throw err;
        }
        return body;
      });
    });
  }

  function refresh() {
    if (inflight) return inflight;
    inflight = fetchJSON('/cart.js').then(function (d) {
      inflight = null;
      return setState(d);
    }).catch(function (err) {
      inflight = null;
      throw err;
    });
    return inflight;
  }

  function findItemIndex(key) {
    /* Accept numeric variant id or string line key (Shopify uses `line_key` strings like "42:abc") */
    var strKey = String(key);
    for (var i = 0; i < state.items.length; i++) {
      if (String(state.items[i].key) === strKey || String(state.items[i].id) === strKey || String(state.items[i].variant_id) === strKey) {
        return i;
      }
    }
    return -1;
  }

  /* 5 ml offert (assets/eloence-gift.js) : resynchronise la ligne cadeau
     apres chaque mutation du panier, AVANT de rendre la main a l'appelant —
     la fiche produit ouvre donc le tiroir avec le cadeau deja present.
     remove() passe par updateQty() : un seul appel. */
  function afterMutation(s) {
    var g = window.ELOENCE_GIFT;
    if (!g || typeof g.sync !== 'function') return s;
    return g.sync().then(function () { return state; }, function () { return state; });
  }

  function add(id, quantity, properties) {
    var q = quantity || 1;
    var body = { items: [{ id: id, quantity: q }] };
    if (properties) body.items[0].properties = properties;
    return fetchJSON('/cart/add.js', { method: 'POST', body: JSON.stringify(body) })
      .then(function (addedItem) {
        /* /cart/add.js returns the added line item (not the whole cart).
           Refresh to pick up discounts, shipping, totals. */
        var title = addedItem && (addedItem.product_title || addedItem.title);
        return refresh().then(function (s) {
          if (title) announce('Ajouté au panier : ' + title);
          return s;
        }).then(afterMutation);
      })
      .catch(function (err) {
        /* No optimistic mutation here — the initial state was not changed */
        throw err;
      });
  }

  /* Ajout de plusieurs lignes en un seul POST /cart/add.js (selecteur d'offre).
     Shopify rejette la requete entiere sur une variante invalide ; en cas
     d'erreur on compare quand meme le panier a son etat d'avant et on remet
     les quantites d'origine, pour qu'aucun ajout partiel ne reste. */
  function addItems(items) {
    var before = null;
    return fetchJSON('/cart.js')
      .then(function (cart) {
        before = {};
        (cart.items || []).forEach(function (it) {
          before[it.variant_id] = (before[it.variant_id] || 0) + it.quantity;
        });
        return fetchJSON('/cart/add.js', { method: 'POST', body: JSON.stringify({ items: items }) });
      })
      .then(function (added) {
        var list = (added && added.items) || [];
        var title = list.length === 1 ? (list[0].product_title || list[0].title) : '';
        return refresh().then(function (s) {
          announce(title ? 'Ajouté au panier : ' + title : list.length + ' parfums ajoutés au panier');
          return s;
        }).then(afterMutation);
      })
      .catch(function (err) {
        if (!before) throw err;
        return fetchJSON('/cart.js').then(function (cart) {
          var updates = {};
          var dirty = false;
          var now = {};
          (cart.items || []).forEach(function (it) {
            now[it.variant_id] = (now[it.variant_id] || 0) + it.quantity;
          });
          items.forEach(function (it) {
            var was = before[it.id] || 0;
            if ((now[it.id] || 0) !== was) { updates[it.id] = was; dirty = true; }
          });
          if (!dirty) return refresh();
          return fetchJSON('/cart/update.js', { method: 'POST', body: JSON.stringify({ updates: updates }) })
            .then(function () { return refresh(); });
        }).then(function () { throw err; }, function () { throw err; });
      });
  }

  function updateQty(key, quantity) {
    var idx = findItemIndex(key);
    var prevItems = state.items.slice();
    var itemTitle = idx >= 0 ? state.items[idx].product_title : '';
    var isRemoval = quantity <= 0;

    /* Optimistic apply — clone, update, broadcast before the POST lands */
    if (idx >= 0) {
      var nextItems = state.items.map(function (it, i) {
        if (i !== idx) return it;
        var c = cloneItem(it);
        c.quantity = Math.max(0, quantity);
        /* Recompute this line's total so totals stay consistent */
        var unitPrice = it.price || (it.final_price != null ? it.final_price : 0);
        c.final_line_price = unitPrice * c.quantity;
        c.original_line_price = (it.original_price || unitPrice) * c.quantity;
        return c;
      }).filter(function (it) { return it.quantity > 0; });
      applyOptimistic(nextItems);
    }

    return fetchJSON('/cart/change.js', {
      method: 'POST',
      body: JSON.stringify({ id: key, quantity: quantity }),
    }).then(function (data) {
      /* Reconcile from server truth — discount rules or inventory caps may
         have adjusted the line. setState broadcasts to listeners. */
      setState(data);
      if (isRemoval) announce('Article retiré du panier');
      else if (itemTitle) announce('Panier mis à jour : ' + itemTitle);
      return state;
    }).then(afterMutation, function (err) {
      /* Revert by re-applying previous items, then refreshing for server truth */
      applyOptimistic(prevItems);
      refresh().catch(function (e) { debug(e); });
      throw err;
    });
  }

  function remove(key) {
    return updateQty(key, 0);
  }

  function clear() {
    var prevItems = state.items.slice();
    applyOptimistic([]);
    return fetchJSON('/cart/clear.js', { method: 'POST' })
      .then(function (data) {
        setState(data);
        announce('Panier vidé');
        return state;
      })
      .catch(function (err) {
        applyOptimistic(prevItems);
        refresh().catch(function (e) { debug(e); });
        throw err;
      });
  }

  function subscribe(fn) {
    listeners.push(fn);
    /* Always push something synchronously so UI can render an initial frame.
       If we already have a real state, use it; otherwise push empty state so
       the drawer renders the empty message before the first /cart.js lands. */
    try {
      if (ready) fn(state);
      else fn(EMPTY_STATE);
    } catch (e) { debug(e); }
    return function () {
      var i = listeners.indexOf(fn);
      if (i >= 0) listeners.splice(i, 1);
    };
  }

  function openDrawer() {
    window.dispatchEvent(new CustomEvent('eloence:cart:open'));
  }

  function closeDrawer() {
    window.dispatchEvent(new CustomEvent('eloence:cart:close'));
  }

  window.addEventListener('storage', function (e) {
    if (e.key === STORAGE_KEY) {
      try {
        var payload = JSON.parse(e.newValue || 'null');
        if (payload && payload.state) {
          state = payload.state;
          ready = true;
          for (var i = 0; i < listeners.length; i++) {
            try { listeners[i](state); } catch (err) { debug(err); }
          }
          return;
        }
      } catch (err) { debug(err); /* fall through to refresh */ }
      refresh();
    }
  });

  /* Wire the drawer's aria-live region — a polite region that receives
     product-level announcements dispatched from add/updateQty/remove. */
  function wireAnnounceRegion() {
    window.addEventListener('eloence:cart:announce', function (e) {
      var msg = e && e.detail && e.detail.message;
      if (!msg) return;
      var region = document.querySelector('[data-el-drawer-announce]');
      if (!region) return;
      /* Clear then set on next tick so screen readers re-announce identical text */
      region.textContent = '';
      setTimeout(function () { region.textContent = msg; }, 30);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { wireAnnounceRegion(); });
  } else {
    wireAnnounceRegion();
  }

  /* ---------- Cart-level mutations (note, attributes, discount) ---------- */
  function updateNote(note) {
    return fetchJSON('/cart/update.js', {
      method: 'POST',
      body: JSON.stringify({ note: note == null ? '' : String(note) }),
    }).then(function (data) {
      setState(data);
      return state;
    });
  }

  function updateAttributes(attrs) {
    if (!attrs || typeof attrs !== 'object') return Promise.resolve(state);
    return fetchJSON('/cart/update.js', {
      method: 'POST',
      body: JSON.stringify({ attributes: attrs }),
    }).then(function (data) {
      setState(data);
      return state;
    });
  }

  /* Per Shopify Ajax Cart API docs, the documented key for `/cart/update.js`
     is `discount` (not `discount_code`). Sending an empty string removes the
     currently applied discount. We send both keys to be defensive — extra
     payload keys are ignored by Shopify. */
  function applyDiscount(code) {
    var trimmed = (code == null ? '' : String(code)).trim();
    return fetchJSON('/cart/update.js', {
      method: 'POST',
      body: JSON.stringify({ discount: trimmed, discount_code: trimmed }),
    }).then(function (data) {
      setState(data);
      return state;
    });
  }

  function removeDiscount() {
    return applyDiscount('');
  }

  window.ELOENCE_CART = {
    subscribe: subscribe,
    add: add,
    addItems: addItems,
    /* Pose un panier deja recu du serveur (reponse de /cart/update.js ou
       /cart/change.js) : un rendu, sans GET supplementaire. */
    _setState: setState,
    updateQty: updateQty,
    remove: remove,
    clear: clear,
    refresh: refresh,
    openDrawer: openDrawer,
    closeDrawer: closeDrawer,
    updateNote: updateNote,
    updateAttributes: updateAttributes,
    applyDiscount: applyDiscount,
    removeDiscount: removeDiscount,
    getState: function () { return state; },
    isReady: function () { return ready; },
    announce: announce,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { refresh(); });
  } else {
    refresh();
  }
})();

/* ------------------------------------------------------------------ */
/*                   Eloence Cart — UI extensions                      */
/* ------------------------------------------------------------------ */
/* Adds: recovery cookie + header red dot, gift-wrap toggle (cart
   attribute), cart-note debounced sync, discount code apply/remove.
   Wires to both [data-el-cart-drawer] and [data-el-cart-page]. */
(function () {
  var COOKIE_NAME = 'eloence_has_cart';
  var COOKIE_DAYS = 7;
  var STALE_CLASS = 'has-el-stale-cart';
  var NOTE_DEBOUNCE_MS = 600;

  function debug(e) {
    if (window.console && console.debug) console.debug('[eloence-cart-ui]', e);
  }

  /* ---------- Recovery cookie ---------- */
  function setCookie(name, value, days) {
    try {
      var expires = '';
      if (days) {
        var d = new Date();
        d.setTime(d.getTime() + days * 86400000);
        expires = '; expires=' + d.toUTCString();
      }
      document.cookie = name + '=' + encodeURIComponent(value) + expires + '; path=/; SameSite=Lax';
    } catch (e) { debug(e); }
  }

  function getCookie(name) {
    try {
      var prefix = name + '=';
      var parts = document.cookie ? document.cookie.split('; ') : [];
      for (var i = 0; i < parts.length; i++) {
        if (parts[i].indexOf(prefix) === 0) return decodeURIComponent(parts[i].slice(prefix.length));
      }
    } catch (e) { debug(e); }
    return '';
  }

  function clearCookie(name) {
    try {
      document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax';
    } catch (e) { debug(e); }
  }

  /* `has-items` masque le point rouge quand la pastille chiffrée est visible.
     Il se calcule sur l'état du panier (serveur) dès qu'il est connu, jamais sur
     le texte de la pastille : au rechargement, ce subscriber peut passer AVANT
     celui qui met la pastille à jour ; il lisait alors « 0 », laissait le point
     rouge, et le point recouvrait le chiffre (un « O » dans l'icône, 2026-09-24). */
  function applyStaleCartDot(state) {
    var hasCookie = getCookie(COOKIE_NAME) === '1';
    var headerCart = document.querySelector('[data-el-cart-open]');
    if (!headerCart) return;
    if (hasCookie) headerCart.classList.add(STALE_CLASS);
    else headerCart.classList.remove(STALE_CLASS);
    var count;
    if (state && window.ELOENCE_CART && window.ELOENCE_CART.isReady && window.ELOENCE_CART.isReady()) {
      count = state.item_count || 0;
    } else {
      var badge = document.querySelector('[data-el-cart-count]');
      count = Number(badge && badge.textContent || '0');
    }
    headerCart.classList.toggle('has-items', count > 0);
  }

  function wireRecoveryCookie() {
    applyStaleCartDot();
    if (!window.ELOENCE_CART) return;
    window.ELOENCE_CART.subscribe(function (state) {
      if (state && state.item_count > 0) {
        setCookie(COOKIE_NAME, '1', COOKIE_DAYS);
      } else if (window.ELOENCE_CART.isReady && window.ELOENCE_CART.isReady()) {
        /* Only clear once we've confirmed the server-truth cart is empty.
           The subscribe() callback fires synchronously with EMPTY_STATE on
           first subscribe; we must not treat that pre-hydration state as a
           reason to wipe the recovery cookie. */
        clearCookie(COOKIE_NAME);
      }
      applyStaleCartDot(state);
    });
  }

  /* ---------- Gift-wrap toggle ---------- */
  function wireGiftWrap(scope) {
    var inputs = scope.querySelectorAll('[data-el-gift-wrap]');
    if (!inputs.length) return;
    inputs.forEach(function (input) {
      if (input.dataset.elWired === '1') return;
      input.dataset.elWired = '1';
      input.addEventListener('change', function () {
        if (!window.ELOENCE_CART) return;
        var wrapper = input.closest('[data-el-cart-addon]');
        if (wrapper) wrapper.classList.add('is-loading');
        window.ELOENCE_CART.updateAttributes({
          'gift_wrap': input.checked ? 'Oui' : '',
          'Gift wrap': input.checked ? 'Oui' : '',
        }).catch(function (err) {
          debug(err);
          input.checked = !input.checked; /* revert */
        }).then(function () {
          if (wrapper) wrapper.classList.remove('is-loading');
        });
      });
    });
  }

  /* ---------- Cart note ---------- */
  function wireCartNote(scope) {
    var toggles = scope.querySelectorAll('[data-el-note-toggle]');
    toggles.forEach(function (btn) {
      if (btn.dataset.elWired === '1') return;
      btn.dataset.elWired = '1';
      btn.addEventListener('click', function () {
        var target = document.getElementById(btn.getAttribute('aria-controls'));
        if (!target) return;
        var open = target.hasAttribute('hidden');
        if (open) {
          target.removeAttribute('hidden');
          btn.setAttribute('aria-expanded', 'true');
          var ta = target.querySelector('textarea');
          if (ta) setTimeout(function () { ta.focus(); }, 50);
        } else {
          target.setAttribute('hidden', '');
          btn.setAttribute('aria-expanded', 'false');
        }
      });
    });

    var notes = scope.querySelectorAll('[data-el-cart-note]');
    notes.forEach(function (ta) {
      if (ta.dataset.elWired === '1') return;
      ta.dataset.elWired = '1';
      var timer = null;
      ta.addEventListener('input', function () {
        if (timer) clearTimeout(timer);
        timer = setTimeout(function () {
          if (!window.ELOENCE_CART) return;
          window.ELOENCE_CART.updateNote(ta.value).catch(debug);
        }, NOTE_DEBOUNCE_MS);
      });
    });
  }

  /* ---------- Discount code ---------- */
  function setDiscountMessage(scope, text, kind) {
    var msg = scope.querySelector('[data-el-discount-msg]');
    if (!msg) return;
    msg.textContent = text || '';
    msg.classList.remove('is-error', 'is-ok');
    if (kind === 'error') msg.classList.add('is-error');
    if (kind === 'ok') msg.classList.add('is-ok');
  }

  function renderAppliedChip(wrap, applications, removeLabel) {
    var container = wrap.querySelector('[data-el-discount-applied]');
    var form = wrap.querySelector('[data-el-discount-form]');
    if (!container) return;
    if (!applications || applications.length === 0) {
      container.innerHTML = '';
      container.setAttribute('hidden', '');
      if (form) form.removeAttribute('hidden');
      return;
    }
    var isPage = wrap.classList.contains('el-cartpage__discount');
    var tagClass = isPage ? 'el-cartpage__discount-tag' : 'el-cart-drawer__discount-tag';
    var amountClass = isPage ? '' : 'el-cart-drawer__discount-amount';
    var removeClass = isPage ? 'el-cartpage__discount-remove' : 'el-cart-drawer__discount-remove';
    var html = applications.map(function (app) {
      var title = String(app.title || '').replace(/[&<>"']/g, function (c) {
        return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
      });
      var amount = typeof app.total_allocated_amount === 'number'
        ? '−' + (app.total_allocated_amount / 100).toFixed(2).replace('.', ',') + '\u00a0€'
        : '';
      return '<span class="' + tagClass + '">' +
        '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 12.5 9 17.5 20 6.5"/></svg>' +
        '<strong>' + title + '</strong>' +
        (amount ? '<span' + (amountClass ? ' class="' + amountClass + '"' : '') + '>' + amount + '</span>' : '') +
        '<button type="button" class="' + removeClass + '" data-el-discount-remove data-discount-code="' + title + '" aria-label="' + (removeLabel || 'Retirer') + '">' +
          '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>' +
        '</button>' +
      '</span>';
    }).join('');
    container.innerHTML = html;
    container.removeAttribute('hidden');
    if (form) form.setAttribute('hidden', '');
  }

  function wireDiscount(scope) {
    var wraps = scope.querySelectorAll('[data-el-discount]');
    wraps.forEach(function (wrap) {
      if (wrap.dataset.elWired === '1') return;
      wrap.dataset.elWired = '1';

      var input = wrap.querySelector('[data-el-discount-input]');
      var applyBtn = wrap.querySelector('[data-el-discount-apply]');

      function apply() {
        if (!window.ELOENCE_CART || !input) return;
        var code = (input.value || '').trim();
        if (!code) return;
        if (applyBtn) applyBtn.disabled = true;
        setDiscountMessage(wrap, 'Vérification…', null);
        window.ELOENCE_CART.applyDiscount(code).then(function (state) {
          if (applyBtn) applyBtn.disabled = false;
          var applied = state && state.cart && state.cart.cart_level_discount_applications || [];
          if (applied.length > 0) {
            setDiscountMessage(wrap, 'Code appliqué', 'ok');
            renderAppliedChip(wrap, applied, wrap.getAttribute('data-remove-label'));
            window.dispatchEvent(new CustomEvent('eloence:cart:discount', { detail: { applied: true } }));
          } else {
            setDiscountMessage(wrap, 'Code invalide ou non applicable', 'error');
          }
        }).catch(function (err) {
          debug(err);
          if (applyBtn) applyBtn.disabled = false;
          setDiscountMessage(wrap, 'Impossible d\u2019appliquer le code', 'error');
        });
      }

      if (applyBtn) applyBtn.addEventListener('click', function (e) {
        e.preventDefault();
        apply();
      });
      if (input) input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); apply(); }
      });

      /* Delegated remove handler so server-rendered chips AND JS-injected
         chips both work without re-wiring. */
      wrap.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-el-discount-remove]');
        if (!btn) return;
        e.preventDefault();
        if (!window.ELOENCE_CART) return;
        btn.disabled = true;
        window.ELOENCE_CART.removeDiscount().then(function () {
          setDiscountMessage(wrap, '', null);
          renderAppliedChip(wrap, [], null);
          window.dispatchEvent(new CustomEvent('eloence:cart:discount', { detail: { applied: false } }));
        }).catch(function (err) {
          debug(err);
          btn.disabled = false;
        });
      });
    });
  }

  function initScopes() {
    document.querySelectorAll('[data-el-cart-drawer], [data-el-cart-page]').forEach(function (scope) {
      wireGiftWrap(scope);
      wireCartNote(scope);
      wireDiscount(scope);
    });
  }

  function init() {
    wireRecoveryCookie();
    initScopes();
    /* Re-wire after cart-drawer re-renders or section re-renders */
    window.addEventListener('eloence:cart:opened', initScopes);
    document.addEventListener('shopify:section:load', initScopes);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
